import { useEffect, useMemo, useState } from "react";
import { gbp, ukDate } from "../../lib/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { prices } from "./FindingCard";

// Nothing is written until "Confirm corrections" — the owner can untick wrong selections here first.
export default function ConfirmCorrectionsDialog({ findings, decision, open, onClose, onConfirm, busy }) {
  const [kept, setKept] = useState(new Set());
  useEffect(() => { if (open) setKept(new Set(findings.map((f) => f.line_id))); }, [open, findings]);
  const chosen = useMemo(() => findings.filter((f) => kept.has(f.line_id)), [findings, kept]);
  const products = new Set(chosen.map((f) => f.product_id)).size;
  const invoices = new Set(chosen.map((f) => f.invoice_id)).size;
  const approve = decision === "approved";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="confirm-corrections-dialog">
        <DialogHeader>
          <DialogTitle>{approve ? "Confirm packaging corrections" : "Keep these lines as verified history"}</DialogTitle>
          <DialogDescription data-testid="confirm-corrections-summary">
            <strong>{chosen.length}</strong> invoice line{chosen.length === 1 ? "" : "s"} · <strong>{products}</strong> product{products === 1 ? "" : "s"} · <strong>{invoices}</strong> invoice{invoices === 1 ? "" : "s"}.
            {approve ? " Each line takes its product's packaging; the extracted packaging is kept and every change can be undone. Quantities, prices, VAT, totals and confirmed statuses are never touched." : " Lines are marked as a genuine historical packaging version and excluded from future syncs. Reversible at any time."}
          </DialogDescription>
        </DialogHeader>
        <ul className="flex-1 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg" data-testid="confirm-corrections-list">
          {findings.map((f) => (
            <li key={f.line_id} className={`flex items-start gap-3 p-2.5 text-xs ${kept.has(f.line_id) ? "" : "opacity-50"}`} data-testid={`confirm-line-${f.line_id}`}>
              <Checkbox checked={kept.has(f.line_id)} onCheckedChange={(v) => setKept((s) => { const n = new Set(s); v ? n.add(f.line_id) : n.delete(f.line_id); return n; })} className="mt-0.5 h-4 w-4" data-testid={`confirm-line-toggle-${f.line_id}`} />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900 truncate">{f.description} <span className="text-slate-400 font-normal">· {f.invoice_number || "invoice"} · {ukDate(f.invoice_date)} · {f.quantity} × {gbp(f.unit_price)}</span></p>
                <div className="grid sm:grid-cols-2 gap-1 mt-1">
                  <p><span className="text-slate-500">Original:</span> {f.current_summary} <span className="font-mono text-slate-500">({prices(f.before)})</span></p>
                  {approve ? <p><span className="text-emerald-700">Proposed:</span> {f.proposed_summary} <span className="font-mono text-emerald-700">({prices(f.after)})</span></p> : <p className="text-slate-500">stays as extracted</p>}
                </div>
              </div>
            </li>
          ))}
        </ul>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="confirm-corrections-cancel">Cancel</Button>
          <Button className={approve ? "bg-emerald-600 hover:bg-emerald-700" : ""} variant={approve ? "default" : "secondary"} disabled={!chosen.length || busy} onClick={() => onConfirm(chosen)} data-testid="confirm-corrections-submit">
            {approve ? `Confirm corrections (${chosen.length})` : `Keep ${chosen.length} as is`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
